import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { FileDownloadOutlined } from "@mui/icons-material";
import { designColors, designRadii, designShell, designType, designWeights } from "services/constants/designTokens";
import { sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { Field, panelBodySx } from "../Shared/formUi";
import { TextField } from "../Shared/fieldInputs";
import { monthsBetween, shortMonth } from "./bonusCalc";
import { cycleDates } from "./bonusSampleApi";
import {
  Chip,
  DAYS_LABEL,
  DetailRows,
  MonthTable,
  RESULT,
  RUN_STATUS,
  SectionCard,
  downloadRunWorkbook,
  forPeriodLong,
  monthNote,
  money,
  money2,
  paymentLabel,
  resultRows,
  rupees,
} from "./bonusUi";

const headSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "normal", lineHeight: 1.3 };
const cellSx = { ...sharedTableBodyCellSx, ...designType.tableBodyCell, py: 1 };
const right = { textAlign: "right" };

// Summary table of calculated rows (Step 2 and the run view). onRowClick
// opens the employee's month-by-month drawer.
export const SummaryTable = ({ rows, type, onRowClick, selectedId }) => {
  const daysBasis = type?.days_basis || "paid_days";
  const showWages = type?.method === "percent_of_wages";
  const showDays = type?.method === "percent_of_wages" || type?.method === "per_day" || Boolean(type?.min_days);
  return (
    <Mui.TableContainer sx={{ overflowX: "auto" }}>
      <Mui.Table size="small" sx={{ minWidth: 900 }}>
        <Mui.TableHead>
          <Mui.TableRow>
            <Mui.TableCell sx={headSx}>Employee</Mui.TableCell>
            <Mui.TableCell sx={{ ...headSx, ...right }}>Months</Mui.TableCell>
            {showDays ? <Mui.TableCell sx={{ ...headSx, ...right }}>{DAYS_LABEL[daysBasis]}</Mui.TableCell> : null}
            {showWages ? <Mui.TableCell sx={{ ...headSx, ...right }}>Wages Used (₹)</Mui.TableCell> : null}
            <Mui.TableCell sx={{ ...headSx, ...right }}>Bonus Due (₹)</Mui.TableCell>
            <Mui.TableCell sx={{ ...headSx, ...right }}>Earlier Bonus (₹)</Mui.TableCell>
            <Mui.TableCell sx={{ ...headSx, ...right }}>Pay Now (₹)</Mui.TableCell>
            <Mui.TableCell sx={headSx}>Result</Mui.TableCell>
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {rows.length === 0 ? (
            <Mui.TableRow>
              <Mui.TableCell colSpan={8} sx={{ ...cellSx, textAlign: "center", py: 4, color: designColors.slate600 }}>No employees match.</Mui.TableCell>
            </Mui.TableRow>
          ) : rows.map((row) => {
            const paid = row.result === "eligible" || row.reason_code === "EARLIER_MORE_THAN_DUE";
            return (
              <Mui.TableRow
                key={row.employee_id}
                hover
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{ cursor: onRowClick ? "pointer" : "default", ...(selectedId === row.employee_id ? designShell.neutralSelectedRow : {}) }}
              >
                <Mui.TableCell sx={cellSx}>
                  <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium }}>{row.employee_name}</Mui.Typography>
                  <Mui.Typography sx={designType.captionText}>{row.employee_code}</Mui.Typography>
                </Mui.TableCell>
                <Mui.TableCell sx={{ ...cellSx, ...right }}>{row.months_counted}</Mui.TableCell>
                {showDays ? <Mui.TableCell sx={{ ...cellSx, ...right }}>{row.days_counted_total ?? "—"}</Mui.TableCell> : null}
                {showWages ? <Mui.TableCell sx={{ ...cellSx, ...right }}>{paid ? money(row.wages_used_total) : "—"}</Mui.TableCell> : null}
                <Mui.TableCell sx={{ ...cellSx, ...right }}>{paid ? money(row.due) : "—"}</Mui.TableCell>
                <Mui.TableCell sx={{ ...cellSx, ...right }}>{paid ? money(row.already_paid) : "—"}</Mui.TableCell>
                <Mui.TableCell sx={{ ...cellSx, ...right, fontWeight: designWeights.semibold }}>{paid ? money(row.pay_now) : "—"}</Mui.TableCell>
                <Mui.TableCell sx={{ ...cellSx, maxWidth: 320 }}>
                  <Chip {...RESULT[row.result]} />
                  {row.reason ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.5, whiteSpace: "normal" }}>{row.reason}</Mui.Typography> : null}
                </Mui.TableCell>
              </Mui.TableRow>
            );
          })}
        </Mui.TableBody>
      </Mui.Table>
    </Mui.TableContainer>
  );
};

// Month-wise table: each cell = bonus for the month (bold) and wages used
// below; bottom row = all eligible employees (spec B8, Figure A6).
export const MonthwiseTable = ({ rows, allEligible, from, to }) => {
  const months = monthsBetween(from, to);
  const cell = (row, m) => row.months.find((x) => x.month === m);
  const total = (m, key) => allEligible.reduce((sum, row) => sum + (cell(row, m)?.[key] || 0), 0);
  const stacked = (top, bottom, strong = true) => (
    <>
      <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: strong ? designWeights.semibold : 400 }}>{top}</Mui.Typography>
      <Mui.Typography sx={{ ...designType.captionText, fontSize: 11.5 }}>{bottom}</Mui.Typography>
    </>
  );
  return (
    <Mui.TableContainer sx={{ overflowX: "auto" }}>
      <Mui.Table size="small" sx={{ minWidth: 220 + months.length * 84 + 260 }}>
        <Mui.TableHead>
          <Mui.TableRow>
            <Mui.TableCell sx={{ ...headSx, minWidth: 170 }}>Employee</Mui.TableCell>
            {months.map((m) => <Mui.TableCell key={m} sx={{ ...headSx, ...right }}>{shortMonth(m)}</Mui.TableCell>)}
            <Mui.TableCell sx={{ ...headSx, ...right }}>Total</Mui.TableCell>
            <Mui.TableCell sx={{ ...headSx, ...right }}>Earlier</Mui.TableCell>
            <Mui.TableCell sx={{ ...headSx, ...right }}>Pay Now</Mui.TableCell>
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {rows.map((row) => (
            <Mui.TableRow key={row.employee_id}>
              <Mui.TableCell sx={cellSx}>{row.employee_name}</Mui.TableCell>
              {months.map((m) => {
                const c = cell(row, m);
                return (
                  <Mui.TableCell key={m} sx={{ ...cellSx, ...right }}>
                    {c?.bonus_unrounded == null ? "—" : stacked(money2(c.bonus_unrounded), c.wages_used == null ? "" : money(c.wages_used))}
                  </Mui.TableCell>
                );
              })}
              <Mui.TableCell sx={{ ...cellSx, ...right }}>{stacked(money(row.due), row.wages_used_total == null ? "" : money(row.wages_used_total))}</Mui.TableCell>
              <Mui.TableCell sx={{ ...cellSx, ...right }}>{money(row.already_paid)}</Mui.TableCell>
              <Mui.TableCell sx={{ ...cellSx, ...right, color: "#067647", fontWeight: designWeights.semibold }}>{money(row.pay_now)}</Mui.TableCell>
            </Mui.TableRow>
          ))}
          <Mui.TableRow sx={{ backgroundColor: "#F2FBF6" }}>
            <Mui.TableCell sx={{ ...cellSx, fontWeight: designWeights.semibold }}>All {allEligible.length} eligible</Mui.TableCell>
            {months.map((m) => (
              <Mui.TableCell key={m} sx={{ ...cellSx, ...right }}>{stacked(money2(total(m, "bonus_unrounded")), money(total(m, "wages_used")))}</Mui.TableCell>
            ))}
            <Mui.TableCell sx={{ ...cellSx, ...right }}>{stacked(money(allEligible.reduce((s, r) => s + r.due, 0)), money(allEligible.reduce((s, r) => s + (r.wages_used_total || 0), 0)))}</Mui.TableCell>
            <Mui.TableCell sx={{ ...cellSx, ...right, fontWeight: designWeights.semibold }}>{money(allEligible.reduce((s, r) => s + r.already_paid, 0))}</Mui.TableCell>
            <Mui.TableCell sx={{ ...cellSx, ...right, color: "#067647", fontWeight: designWeights.semibold }}>{money(allEligible.reduce((s, r) => s + r.pay_now, 0))}</Mui.TableCell>
          </Mui.TableRow>
        </Mui.TableBody>
      </Mui.Table>
    </Mui.TableContainer>
  );
};

// Employee drawer: month by month + Result (Figure A5 right).
export const EmployeeMonthsPanel = ({ row, type, from, to, onClose }) => {
  if (!row) return null;
  const paid = row.result === "eligible" || row.reason_code === "EARLIER_MORE_THAN_DUE";
  return (
    <GlobalSidePanel
      open
      onClose={onClose}
      title={`${row.employee_name} · ${row.employee_code}`}
      subtitle={`${type?.name || "Bonus"} · ${forPeriodLong(from, to)}`}
      bodySx={panelBodySx}
      footer={<Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}><SecondaryButton onClick={onClose}>Close</SecondaryButton></Mui.Stack>}
    >
      <Mui.Stack spacing={2}>
        {row.months?.length && paid ? (
          <SectionCard title="Month by month">
            <MonthTable row={row} type={type} />
            <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>{monthNote(row, type)}</Mui.Typography>
          </SectionCard>
        ) : null}
        <SectionCard title="Result" tone={row.result === "eligible" ? "green" : undefined}>
          {paid ? (
            <DetailRows rows={resultRows(row, { payLabel: "Pay now (balance)" })} />
          ) : (
            <Mui.Stack spacing={1}>
              <Chip {...RESULT[row.result]} sx={{ alignSelf: "flex-start" }} />
              <Mui.Typography sx={designType.tableBodyCell}>{row.reason}</Mui.Typography>
              {row.employed_months?.length ? (
                <Mui.Typography sx={designType.captionText}>
                  Months employed in the period: {row.employed_months.map(shortMonth).join(", ")}.
                </Mui.Typography>
              ) : null}
            </Mui.Stack>
          )}
          {row.reason_code === "EARLIER_MORE_THAN_DUE" ? <Mui.Box sx={{ mt: 1.5 }}><RequestInfoBanner tone="warning" body={row.reason} /></Mui.Box> : null}
        </SectionCard>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

const ViewToggle = ({ value, onChange }) => (
  <Mui.ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    onChange={(event, next) => { if (next) onChange(next); }}
    sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 2, fontWeight: designWeights.semibold, borderRadius: designRadii.compactButton }, "& .Mui-selected": { color: `${designColors.tealDark} !important`, backgroundColor: `${designColors.white} !important` } }}
  >
    <Mui.ToggleButton value="summary">Summary</Mui.ToggleButton>
    <Mui.ToggleButton value="monthwise">Month-wise</Mui.ToggleButton>
  </Mui.ToggleButtonGroup>
);
export { ViewToggle };

// Bonus runs tab row -> read-only run view with Download and Cancel run.
export const BonusRunViewPanel = ({ run, busy, canEdit, onClose, onCancelRun }) => {
  const [view, setView] = useState("summary");
  const [employeeId, setEmployeeId] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [expanded, setExpanded] = useState(true);
  const type = run?.rule_snapshot;
  const eligible = useMemo(() => (run?.rows || []).filter((row) => row.result === "eligible"), [run]);
  if (!run) return null;
  const status = RUN_STATUS[run.status];
  const selected = run.rows.find((row) => row.employee_id === employeeId);

  const cancel = () => {
    if (!reason.trim()) { setReasonError("Enter a reason to cancel the run."); return; }
    onCancelRun(run, reason.trim());
  };

  return (
    <GlobalSidePanel
      open
      expanded={expanded}
      onToggleExpand={() => setExpanded((value) => !value)}
      onClose={onClose}
      title={`Bonus run ${run.id}`}
      subtitle={<>{run.bonus_type_name} · {forPeriodLong(run.from_month, run.to_month)} · <Chip {...status} /></>}
      bodySx={panelBodySx}
      headerActions={
        <SecondaryButton
          startIcon={<FileDownloadOutlined />}
          onClick={() => downloadRunWorkbook({ fileName: `${run.id}.xlsx`, rows: run.rows, type, from: run.from_month, to: run.to_month })}
        >
          Download
        </SecondaryButton>
      }
      footer={
        canEdit && run.can_cancel ? (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <SecondaryButton disabled={busy} onClick={cancel} sx={{ color: "#B42318", borderColor: "#FECDD3" }}>Cancel run</SecondaryButton>
          </Mui.Stack>
        ) : null
      }
    >
      <Mui.Stack spacing={2}>
        <SectionCard title="Run">
          <DetailRows
            rows={[
              run.name ? { label: "Name", value: run.name } : null,
              { label: "Bonus type", value: `${run.bonus_type_name} (v${run.type_version})` },
              { label: "Months", value: `${forPeriodLong(run.from_month, run.to_month)} (${monthsBetween(run.from_month, run.to_month).length})` },
              { label: "Paying", value: paymentLabel(run.pay_mode, run.part_percent, run.totals.already_paid > 0) },
              { label: "Pay with", value: cycleDates(run.payroll_cycle) },
              { label: "Employees paid", value: `${run.employees} of ${run.totals.eligible} eligible` },
              { label: "Left out", value: `${run.totals.excluded + run.totals.check} (${run.totals.excluded} excluded · ${run.totals.check} need checking)` },
              { label: "Bonus due", value: rupees(run.totals.due) },
              { label: "Earlier bonus", value: rupees(run.totals.already_paid) },
              { label: "Paying now", value: rupees(run.amount), color: "#067647" },
              { label: "Created", value: `${moment(run.created_at).format("DD MMM YYYY, HH:mm")} · ${run.created_by}` },
              run.cancel_reason ? { label: "Cancel reason", value: run.cancel_reason } : null,
            ]}
          />
        </SectionCard>
        <Mui.Box sx={{ ...designShell.sectionCard, p: 2 }}>
          <Mui.Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <ViewToggle value={view} onChange={setView} />
            <Mui.Typography sx={designType.captionText}>Read-only — as calculated when it was added to payroll.</Mui.Typography>
          </Mui.Stack>
          {view === "summary"
            ? <SummaryTable rows={run.rows} type={type} onRowClick={(row) => setEmployeeId(row.employee_id)} selectedId={employeeId} />
            : <MonthwiseTable rows={eligible} allEligible={eligible} from={run.from_month} to={run.to_month} />}
        </Mui.Box>
        {canEdit && run.can_cancel ? (
          <Field label="Reason (needed to cancel the run)" error={reasonError} wide>
            <TextField value={reason} onChange={(value) => { setReason(value.slice(0, 255)); setReasonError(""); }} placeholder="Type a reason" error={reasonError} />
          </Field>
        ) : run.status === "added" ? (
          <RequestInfoBanner body="Some entries of this run are already in payroll, so the run can't be cancelled. Ready entries can still be cancelled one by one." />
        ) : null}
        <Mui.Typography sx={designType.captionText}>
          A run can be cancelled until its payroll is run — all its Ready entries are cancelled together. Processed runs are locked.
        </Mui.Typography>
      </Mui.Stack>
      {selected ? (
        <EmployeeMonthsPanel row={selected} type={type} from={run.from_month} to={run.to_month} onClose={() => setEmployeeId(null)} />
      ) : null}
    </GlobalSidePanel>
  );
};
